import { Component, Inject } from '@angular/core';
import {
   AbstractControl,
   FormArray,
   FormBuilder,
   FormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CallState, LoadingState } from '@lcs/shared/api';
import { DeviceTypes, ExtensionTypes, PhoneTypes } from 'generated/graphql-types';
import { catchError, map, take } from 'rxjs/operators';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { AddExtensionApiService } from 'src/app/pages/add-extension/services/add-extension-api.service';
import { DevicesFilterOption } from 'src/app/pages/device-register/models/device-filter-option';
import { DeviceRegisterApiService } from 'src/app/pages/device-register/services/device-register-api.service';
import { ExtensionDetailFormService } from 'src/app/pages/extension-details/services/extension-detail-form.service';
import { ExtensionRegisterApiService } from 'src/app/pages/extension-register/services/extension-register-api.service';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from '../confirmation-dialog/models/confirmation-dialog.config';
import { LcsDialogService } from '../dialog/dialog.service';
import { processErrorMessage } from '../errors/error-processor';
import { BulkExtensionDetailSaveEvent } from './models/bulk-configure-extensions-form-submit';
import { BulkConfigureExtensionsApiService } from './Services/bulk-configure-extensions-api.service';
@Component({
   providers: [ExtensionDetailFormService],
   selector: 'ndt-bulk-configure-extensions-dialog',
   templateUrl: './bulk-configure-extensions-dialog.component.html',
   styleUrls: ['./bulk-configure-extensions-dialog.component.scss'],
   standalone: false,
})
export class BulkConfigureExtensionsDialogComponent {
   form: FormGroup;
   callState: CallState = LoadingState.Loading;
   devices: any[] = [];
   extensions: number[] = [];
   accountId: number;
   deviceFilters: DevicesFilterOption[] = [];
   addExtensionApiService: AddExtensionApiService;
   hasPhonesToConfigure: boolean;
   isSubmitting = false;
   constructor(
      private dialogRef: MatDialogRef<BulkConfigureExtensionsDialogComponent>,
      @Inject(MAT_DIALOG_DATA) private data: any,
      private deviceRegisterApiService: DeviceRegisterApiService,
      private extensionRegisterApiService: ExtensionRegisterApiService,
      private dialogService: LcsDialogService,
      private fb: FormBuilder,
      private snackbar: MatSnackBar,
      private confirmationService: ConfirmationDialogService,
      private bulkConfigureExtensionsApiService: BulkConfigureExtensionsApiService
   ) {
      this.accountId = data.accountId;
      this.deviceFilters.push(
         {
            field: 'AccountId',
            operation: FilterOperations.EqualTo,
            type: 'numeric',
            value: this.accountId,
         },
         {
            field: 'PhoneId',
            operation: FilterOperations.HasValue,
            type: 'device',
            value: false,
         }
      );
   }

   async loadDevices() {
      await this.deviceRegisterApiService.getDevices(null, this.deviceFilters, null, null, 500).subscribe((devices) => {
         this.callState = LoadingState.Loaded;
         devices.data.devicesConnection.items.forEach((element) => {
            if (!element.phone) {
               this.devices.push(element);
            }
         });
         if (this.devices.length > 0) {
            this.hasPhonesToConfigure = true;
            this.createForms();
         } else {
            this.hasPhonesToConfigure = false;
         }
      });
      //TODO: on error, set to loaded as well
   }

   async loadExtensions() {
      await this.extensionRegisterApiService.getExtensionNumbers(null, this.accountId).subscribe((extensions) => {
         extensions.data.extensionsConnection.items.forEach((element) => {
            this.extensions.push(element.number);
         });
      });
   }

   onIsConfigureThisPhone(index: number, event: MatCheckbox) {
      const controls: AbstractControl[] = [];
      const numberControl = this.formArray.controls[index].get('number');
      const nameControl = this.formArray.controls[index].get('name');
      const displayNameControl = this.formArray.controls[index].get('displayName');
      const defaultPhoneNumberControl = this.formArray.controls[index].get('defaultPhoneNumber');
      const numberOfLineKeysControl = this.formArray.controls[index].get('numberOfLineKeys');
      const emailAddressControl = this.formArray.controls[index].get('emailAddress');
      const isCustomDescriptionControl = this.formArray.controls[index].get('isCustomDescription');
      const isConfigureThisPhone = this.formArray.controls[index].get('isConfigureThisPhone');
      if (isCustomDescriptionControl) {
         isCustomDescriptionControl.setValue(false);
      }
      if (numberControl) {
         controls.push(numberControl);
      }
      if (nameControl) {
         controls.push(nameControl);
      }
      if (displayNameControl) {
         controls.push(displayNameControl);
      }
      if (numberOfLineKeysControl) {
         controls.push(numberOfLineKeysControl);
      }
      if (defaultPhoneNumberControl) {
         controls.push(defaultPhoneNumberControl);
      }
      if (emailAddressControl) {
         controls.push(emailAddressControl);
      }
      if (!event.checked) {
         this.clearValuesAndResetValidators(controls);
         isConfigureThisPhone?.markAsPristine();
         this.updateDuplicateExtNumberValidators();
      } else {
         this.updateValidityOfForm(controls);
      }
   }

   clearValuesAndResetValidators(controls: AbstractControl[]) {
      controls.forEach((control) => {
         control.setValue(null);
         control.setValidators(null);
         control.updateValueAndValidity();
         control.markAsPristine();
      });
   }

   updateValidityOfForm(controls: AbstractControl[]) {
      controls.forEach((control) => {
         control.updateValueAndValidity();
      });
   }

   updateDuplicateExtNumberValidators() {
      const controlsToValidate = this.formArray.controls.map((formGroup: AbstractControl) =>
         (formGroup as FormGroup).get('number')
      );
      controlsToValidate.forEach((control) => {
         control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
   }

   ngOnInit(): void {
      this.form = this.fb.group({
         formArray: this.fb.array([]),
      });
      this.loadDevices();
      this.loadExtensions();
   }

   get formArray(): FormArray {
      return this.form.get('formArray') as FormArray;
   }

   createForms() {
      this.devices.forEach((device, index) => {
         const devicename = DeviceTypes[device.deviceTypeId];
         const formGroup = this.fb.group({
            isConfigureThisPhone: this.fb.control(null),
            model: { value: devicename, disabled: true },
            macAddress: { value: device.macAddress, disabled: true },
            number: this.fb.control(null),
            name: this.fb.control(null),
            displayName: this.fb.control(null),
            numberOfLineKeys: this.fb.control(null),
            defaultPhoneNumber: this.fb.control(null),
            emailAddress: this.fb.control(null),
            ringTimeout: 20,
            deviceId: device.deviceId,
         });

         formGroup.get('name')?.valueChanges.subscribe((value) => {
            const displayNameControl = formGroup.get('displayName');
            if (displayNameControl && !this.isSubmitting) {
               displayNameControl.setValue(value, { emitEvent: false });
            }
         });

         this.setupFormGroupWatcher(formGroup);
         this.formArray.push(formGroup);
      });
   }

   setupFormGroupWatcher(formGroup: FormGroup): void {
      formGroup.valueChanges.subscribe(() => {
         this.setValidatorsForForm(formGroup);
      });
   }

   setValidatorsForForm(formGroup: FormGroup): void {
      const isConfigureThisPhone = formGroup.get('isConfigureThisPhone');
      if (
         isConfigureThisPhone?.value == null ||
         (isConfigureThisPhone?.pristine && isConfigureThisPhone?.value == false)
      ) {
         isConfigureThisPhone?.setValue(true);
      }
      if (isConfigureThisPhone?.value != false) {
         formGroup.get('model')?.markAsDirty();
         formGroup.get('macAddress')?.markAsDirty();
      }

      const nameControl = formGroup.get('name');
      if (nameControl && isConfigureThisPhone?.value != false) {
         nameControl.setValidators([Validators.required, Validators.pattern(/^[^\s].*/)]);
         nameControl.markAsDirty();
      }

      const displayName = formGroup.get('displayName');
      if (displayName && displayName.touched && isConfigureThisPhone?.value != false) {
         displayName.setValidators([Validators.required, Validators.pattern(/^[^\s].*/)]);
         displayName.markAsDirty();
      }

      const numberOfLineKeysControl = formGroup.get('numberOfLineKeys');
      if (numberOfLineKeysControl && isConfigureThisPhone?.value != false) {
         numberOfLineKeysControl.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(30),
            Validators.pattern(/^[0-9]+$/),
         ]);
         numberOfLineKeysControl.markAsDirty();
      }

      const emailAddressControl = formGroup.get('emailAddress');
      if (emailAddressControl && emailAddressControl.dirty && isConfigureThisPhone?.value != false) {
         emailAddressControl.setValidators([
            Validators.pattern(
               /^(([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*)?$/i
            ),
         ]);
         emailAddressControl.markAsDirty();
      }

      const numberControl = formGroup.get('number');
      if (numberControl && isConfigureThisPhone?.value != false) {
         numberControl.setValidators([
            Validators.required,
            Validators.min(100),
            Validators.max(99999),
            Validators.pattern(/^\d+$/),
            Validators.pattern(/^(?!911).*/),
            Validators.pattern(/^(?!933$)/),
            Validators.pattern(/^(?!(70[0-9]|7[1-9][0-9])$)/),
            this.duplicateExtNumberValidator(this.extensions),
            this.uniqueExtensionNumberValidator(),
         ]);
         numberControl.markAsDirty();
      }
   }

   uniqueExtensionNumberValidator(): ValidatorFn {
      return (control: AbstractControl): { [key: string]: any } | null => {
         const currentValue = control.value;
         const controlsToValidate = this.formArray.controls
            .filter((formGroup: AbstractControl) => {
               const isConfigureThisPhoneControl = (formGroup as FormGroup).get('isConfigureThisPhone');
               return isConfigureThisPhoneControl && isConfigureThisPhoneControl.value;
            })
            .map((formGroup: AbstractControl) => (formGroup as FormGroup).get('number'));

         const duplicateControls = controlsToValidate.filter(
            (ctrl) => ctrl && ctrl !== control && ctrl.value === currentValue && ctrl.dirty
         );

         if (duplicateControls.length > 0) {
            duplicateControls.forEach((duplicateControl) => {
               if (duplicateControl) {
                  const currentErrors = duplicateControl.errors || {};
                  duplicateControl.setErrors({ ...currentErrors, nonUnique: true });
               }
            });
            return { nonUnique: true };
         } else {
            const allDuplicateControls = this.findDuplicateControls(controlsToValidate);
            controlsToValidate.forEach((ctl) => {
               if (ctl && !allDuplicateControls.includes(ctl)) {
                  const currentErrors = ctl.errors || {};
                  delete currentErrors['nonUnique'];
                  if (Object.keys(currentErrors).length === 0) {
                     ctl.setErrors(null, { emitEvent: false });
                  }
                  ctl.markAsTouched();
               }
            });
         }

         return null;
      };
   }

   duplicateExtNumberValidator(extensionNumbers: number[]): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         if (control.value === null || control.value === undefined) {
            return null;
         }
         const value = Number(control.value);
         const isDuplicate = extensionNumbers.includes(value);
         return isDuplicate ? { extensionAlreadyExists: true } : null;
      };
   }

   findDuplicateControls(controlsToValidate: (AbstractControl<any, any> | null)[]): AbstractControl[] {
      const allDuplicateControls: AbstractControl[] = [];

      controlsToValidate.forEach((control, index) => {
         if (!control) return;

         const controlValue = control.value;

         controlsToValidate.forEach((otherControl, otherIndex) => {
            if (
               otherControl &&
               index !== otherIndex &&
               otherControl.value === controlValue &&
               controlValue !== null &&
               controlValue !== ''
            ) {
               if (!allDuplicateControls.includes(control)) {
                  allDuplicateControls.push(control);
               }
               if (!allDuplicateControls.includes(otherControl)) {
                  allDuplicateControls.push(otherControl);
               }
            }
         });
      });

      return allDuplicateControls;
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   submitForm() {
      if (!this.isSubmitting) {
         this.onCreate();
      }
   }

   onCreate(): void {
      this.isSubmitting = true;
      let [extensionsToSaveArray, isValid] = this.formatSubmission();

      if (isValid && extensionsToSaveArray.length > 0) {
         this.bulkConfigureExtensionsApiService
            .create(extensionsToSaveArray)
            .pipe(
               map((executionResult) => {
                  this.dialogService.closeFormComponent();
                  this.snackbar.open(`Extensions Created`);
               }),
               catchError((error) => {
                  this.isSubmitting = false;
                  const errorData = processErrorMessage(error);
                  const dialogRef = this.confirmationService.confirm({
                     data: {
                        dialogType: ConfirmationDialogType.Ok,
                        header: errorData.details ?? 'Error',
                        message: errorData.userMessages.join('\n') ?? 'An Error Occurred',
                     },
                     size: DialogSize.Small,
                  });
                  return dialogRef.afterClosed();
               }),
               take(1)
            )
            .subscribe();
      } else {
         this.isSubmitting = false;
      }
   }

   formatSubmission(): [BulkExtensionDetailSaveEvent[], boolean] {
      const formArray = this.form.get('formArray') as FormArray;
      let extensionsToSaveArray: BulkExtensionDetailSaveEvent[] = [];
      let isValid = true;
      formArray.controls.forEach((control: AbstractControl, index: number) => {
         const formGroup = control as FormGroup;
         const isConfigureThisPhone = formGroup.get('isConfigureThisPhone');
         const isConfigureValue = isConfigureThisPhone?.value;
         if (formGroup.dirty) {
            this.markAllControlsAsTouched(formGroup as FormGroup);
         }
         if (formGroup.dirty && formGroup.valid && isConfigureValue) {
            const extensionToSave: BulkExtensionDetailSaveEvent = {
               accountId: this.accountId,
               extensionType: ExtensionTypes.Phone,
               phoneType: PhoneTypes.Hardphone,
               macAddress: formGroup.getRawValue().macAddress,
               number: formGroup.get('number')?.value,
               name: formGroup.get('name')?.value,
               displayName: formGroup.get('displayName')?.value,
               numberOfLineKeys: formGroup.get('numberOfLineKeys')?.value,
               defaultPhoneNumberId: formGroup.get('defaultPhoneNumber')?.value
                  ? formGroup.get('defaultPhoneNumber')?.value.phoneNumberId
                  : null,
               emailAddress: formGroup.get('emailAddress')?.value,
               ringTimeout: formGroup.get('ringTimeout')?.value,
               deviceId: formGroup.get('deviceId')?.value,
            };
            extensionsToSaveArray.push(extensionToSave);
         } else if (formGroup.dirty && !formGroup.valid && isConfigureValue) {
            isValid = false;
         }
      });
      return [extensionsToSaveArray, isValid];
   }

   markAllControlsAsTouched(formGroup: FormGroup) {
      Object.keys(formGroup.controls).forEach((field) => {
         const control = formGroup.get(field);
         control?.markAsTouched();
         control?.updateValueAndValidity();
      });
   }
}
